import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Button,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  LocationOn as LocationOnIcon,
  FilterList as FilterListIcon,
  Assessment as AssessmentIcon,
  Clear as ClearIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import Plot from 'react-plotly.js';
import axios from 'axios';
import { useTranslation } from '../utils/translations';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const Heatmap = ({ sidebarOpen, language }) => {
  const t = useTranslation(language);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [plotRevision, setPlotRevision] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    gender: 'All',
    age: 'All',
    race: 'All',
    income: 'All',
    party: 'All'
  });

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    gender_options: [],
    age_options: [],
    race_options: [],
    income_options: [],
    party_options: []
  });

  // Data state
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  // Force plot to resize when sidebar state changes with proper debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPlotRevision(prev => prev + 1);
      // Small delay before triggering resize to allow container to settle
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }, 300); // Wait for sidebar animation to complete

    return () => clearTimeout(timeoutId);
  }, [sidebarOpen]);

  // Load filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchSurveyData();
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/heatmap/filter-options`);
      setFilterOptions(response.data);
    } catch (err) {
      setError('Failed to load filter options. Please try again.');
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchSurveyData = async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'All') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${API_BASE_URL}/heatmap/survey-data?${params}`);
      setSurveyData(response.data);
    } catch (err) {
      setError('Failed to fetch survey data. Please try again.');
      console.error('Error fetching survey data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      gender: 'All',
      age: 'All',
      race: 'All',
      income: 'All',
      party: 'All'
    });
  };

  const getActiveFilters = () => {
    return Object.entries(filters)
      .filter(([key, value]) => value !== 'All')
      .map(([key, value]) => `${t(key)}: ${t(value)}`);
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== 'All');
  };

  const handleFullscreenOpen = () => {
    setFullscreenOpen(true);
  };

  const handleFullscreenClose = () => {
    setFullscreenOpen(false);
  };

  const createHeatmapPlot = () => {
    if (!surveyData || !surveyData.state_counts) {
      return null;
    }
    
    try {

    // Complete list of US states (abbreviations)
    const allStates = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
    ];

    // Create a map of survey data for quick lookup
    const surveyDataMap = {};
    surveyData.state_counts.forEach(item => {
      surveyDataMap[item.state] = item.response_count;
    });

    // Create complete data arrays including states with no data
    const states = allStates;
    const rawCounts = allStates.map(state => surveyDataMap[state] || null);
    const counts = rawCounts.map(count => count === null ? 0 : count);

    const data = [{
      type: 'choropleth',
      locationmode: 'USA-states',
      locations: states,
      z: counts,
      zmin: 0,  // Ensure null values map to the lowest color (white)
      colorscale: [
        [0, '#ffffff'],     // White for states with no data
        [0.0001, '#fffbeb'], // Very light yellow for states with minimal data
        [0.2, '#fef3c7'],
        [0.4, '#f59e0b'],
        [0.6, '#ea580c'],
        [0.8, '#dc2626'],
        [1, '#7f1d1d']
      ],
      colorbar: {
        title: {
          text: t('chartResponseCount'),
          font: { 
            family: '"Poppins", sans-serif',
            size: 12,
            color: isDarkMode ? '#e2e8f0' : '#2c2c2c'
          }
        },
        thickness: 22,
        len: 0.8,
        tickfont: {
          family: '"Poppins", sans-serif',
          size: 11,
          color: isDarkMode ? '#cbd5e0' : '#4a5568'
        },
        bgcolor: isDarkMode ? '#414141' : '#ffffff',
        bordercolor: isDarkMode ? '#4a5568' : '#e2e8f0',
        borderwidth: 1,
        outlinecolor: isDarkMode ? '#4a5568' : '#cbd5e0',
        outlinewidth: 1
      },
             hovertemplate: states.map((state, index) => {
         const count = rawCounts[index];
         if (count === null || count === 0) {
                     return `<b style="font-family: 'Playfair Display', serif; font-size: 16px; color: ${isDarkMode ? '#f7fafc' : '#111827'}">${state}</b><br>` +
                  `<span style="color: ${isDarkMode ? '#e2e8f0' : '#374151'}"><b>${t('chartResponseCount')}:</b> <span style="color: #999">${t('noData')}</span></span><br>` +
                  '<extra></extra>';
        }
        return `<b style="font-family: 'Playfair Display', serif; font-size: 16px; color: ${isDarkMode ? '#f7fafc' : '#111827'}">${state}</b><br>` +
               `<span style="color: ${isDarkMode ? '#e2e8f0' : '#374151'}"><b>${t('chartResponseCount')}:</b> <span style="color: ${isDarkMode ? '#e2e8f0' : '#374151'}">${count}</span></span><br>` +
               '<extra></extra>';
      })
    }];

    const layout = {
      geo: {
        scope: 'usa',
        projection: { type: 'albers usa' },
        showlakes: true,
        lakecolor: isDarkMode ? '#1a202c' : 'rgb(255, 255, 255)',
        bgcolor: isDarkMode ? '#414141' : '#ffffff',
        coastlinecolor: isDarkMode ? '#4a5568' : '#000000',
        landcolor: isDarkMode ? '#414141' : '#ffffff',
        showland: true,
        showocean: true,
        oceancolor: isDarkMode ? '#1a202c' : '#ffffff'
      },
      font: { 
        family: '"Poppins", "Inter", sans-serif',
        size: 12,
        color: isDarkMode ? '#e2e8f0' : '#374151'
      },
      margin: { l: 0, r: 80, t: 20, b: 0 },
      autosize: true,
      width: undefined,
      height: undefined,
      datarevision: plotRevision,
      plot_bgcolor: isDarkMode ? '#414141' : '#ffffff',
      paper_bgcolor: isDarkMode ? '#414141' : '#ffffff',
      hovermode: 'closest',
      hoverlabel: {
        bgcolor: isDarkMode ? '#374151' : '#ffffff',
        bordercolor: isDarkMode ? '#4b5563' : '#e5e7eb',
        font: { color: isDarkMode ? '#f3f4f6' : '#111827' }
      }
    };

    const config = {
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d', 'autoScale2d', 'resetScale2d'],
      responsive: true,
      autosizable: true,
      toImageButtonOptions: {
        format: 'png',
        filename: 'survey-response-heatmap',
        height: 600,
        width: 1000,
        scale: 2
      },
      scrollZoom: false,
      doubleClick: 'reset',
      showTips: true,
      plotGlPixelRatio: 2
    };

    return (
      <Plot
        data={data}
        layout={layout}
        config={config}
        style={{ 
          width: '100%', 
          height: '100%'
        }}
        useResizeHandler={true}
        key={`heatmap-${plotRevision}`}
      />
    );
    
    } catch (error) {
      console.error('Error creating heatmap:', error);
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="500px">
          <Typography variant="body1" color="error">
            {t('errorLoadingHeatmap')}
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Container maxWidth={false} sx={{ mb: 5, px: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('surveyResponseHeatmap')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('interactiveVisualization')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Filter Controls */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: isDarkMode ? '#373737' : '#ffffff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1 }} />
                {t('demographicFilters')}
              </Typography>
              
              {hasActiveFilters() && (
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAllFilters}
                  size="small"
                  color="primary"
                >
                  {t('clearAllFilters')}
                </Button>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('gender')}</InputLabel>
                  <Select
                    value={filters.gender}
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                    label={t('gender')}
                  >
                    {filterOptions.gender_options.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('ageGroup')}</InputLabel>
                  <Select
                    value={filters.age}
                    onChange={(e) => handleFilterChange('age', e.target.value)}
                    label={t('ageGroup')}
                  >
                    {filterOptions.age_options.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('raceEthnicity')}</InputLabel>
                  <Select
                    value={filters.race}
                    onChange={(e) => handleFilterChange('race', e.target.value)}
                    label={t('raceEthnicity')}
                  >
                    {filterOptions.race_options.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('incomeLevel')}</InputLabel>
                  <Select
                    value={filters.income}
                    onChange={(e) => handleFilterChange('income', e.target.value)}
                    label={t('incomeLevel')}
                  >
                    {filterOptions.income_options.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth>
                  <InputLabel>{t('partyAffiliation')}</InputLabel>
                  <Select
                    value={filters.party}
                    onChange={(e) => handleFilterChange('party', e.target.value)}
                    label={t('partyAffiliation')}
                  >
                    {filterOptions.party_options.map(option => (
                      <MenuItem key={option} value={option}>{t(option)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Active Filters Display */}
            {getActiveFilters().length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('activeFilters')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(filters)
                    .filter(([key, value]) => value !== 'All')
                    .map(([key, value], index) => (
                      <Chip
                        key={index}
                        label={`${t(key)}: ${t(value)}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => handleFilterChange(key, 'All')}
                        sx={{
                          '& .MuiChip-deleteIcon': {
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out',
                          },
                          '&:hover .MuiChip-deleteIcon': {
                            opacity: 1,
                          }
                        }}
                      />
                    ))}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Statistics Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: isDarkMode ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('totalResponses')}</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {surveyData ? surveyData.total_responses.toLocaleString() : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: isDarkMode ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('statesCovered')}</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {surveyData ? surveyData.states_covered : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: isDarkMode ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('avgPerState')}</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {surveyData && surveyData.states_covered > 0 
                      ? Math.round(surveyData.total_responses / surveyData.states_covered).toLocaleString() 
                      : '-'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ backgroundColor: isDarkMode ? '#373737' : '#ffffff' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FilterListIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">{t('filtersApplied')}</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {getActiveFilters().length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Heatmap Visualization */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 2,
            backgroundColor: isDarkMode ? '#373737' : '#ffffff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ textAlign: 'center', flexGrow: 1 }}>
                {t('geographicDistribution')}
              </Typography>
              <IconButton
                onClick={handleFullscreenOpen}
                color="primary"
                sx={{ ml: 2 }}
                title="View Fullscreen"
              >
                <FullscreenIcon />
              </IconButton>
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
                <CircularProgress size={60} />
              </Box>
            ) : surveyData && surveyData.state_counts && surveyData.state_counts.length > 0 ? (
              <Box sx={{ 
                height: '500px', 
                width: '100%', 
                position: 'relative',
                overflow: 'hidden',
                '& .js-plotly-plot': {
                  width: '100% !important',
                  height: '500px !important',
                }
              }}>
                {createHeatmapPlot()}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '500px',
                flexDirection: 'column'
              }}>
                <Typography variant="h6" color="text.secondary">
                  No responses match the current filter combination
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your filter selections
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Fullscreen Heatmap Modal */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleFullscreenClose}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
            margin: '2.5vh 2.5vw',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h5">
            {t('geographicDistribution')}
          </Typography>
          <IconButton
            onClick={handleFullscreenClose}
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Fullscreen Heatmap - No Filters */}
          <Box sx={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            '& .js-plotly-plot': {
              width: '100% !important',
              height: '100% !important',
            }
          }}>
            {loading ? (
              <CircularProgress size={60} />
            ) : surveyData && surveyData.state_counts && surveyData.state_counts.length > 0 ? (
              <Box sx={{ width: '100%', height: '100%' }}>
                {createHeatmapPlot()}
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <Typography variant="h6" color="text.secondary">
                  No responses match the current filter combination
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your filter selections
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Heatmap; 